import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { ArrowLeft, BookOpen, Brain, Check, BookOpenCheck, Lock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useReadingProgress } from '../contexts/ReadingProgressContext';
import { useModuleRelease } from '../contexts/ModuleReleaseContext';
import { apiService } from '../services/api';
import Quiz from './Quiz';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

// Next Module Button Component
const NextModuleButton = ({ currentModuleId }) => {
  const { language } = useLanguage();
  const { getAllModulesWithStatus } = useModuleRelease();
  
  const getNextModule = () => {
    const modules = getAllModulesWithStatus();
    const currentIndex = modules.findIndex(m => m.id === currentModuleId);
    if (currentIndex >= 0 && currentIndex < modules.length - 1) {
      return modules[currentIndex + 1];
    }
    return null;
  };
  
  const nextModule = getNextModule();
  
  if (!nextModule) {
    return (
      <Button size="lg" disabled className="min-w-[160px]">
        {language === 'en' ? 'Course Complete!' : '¡Curso Completado!'}
      </Button>
    );
  }
  
  return (
    <Link to={`/module/${nextModule.id}`}>
      <Button 
        size="lg" 
        className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white min-w-[160px]"
      >
        {language === 'en' ? 'Next Module →' : 'Siguiente Módulo →'}
      </Button>
    </Link>
  );
};

const ModulePage = () => {
  const { moduleId } = useParams();
  const { language } = useLanguage();
  const { markSectionAsRead, isSectionRead, getModuleReadingProgress } = useReadingProgress();
  const { isModuleAccessible, getModuleStatus, markModuleCompleted, getAllModulesWithStatus, loading: releaseLoading } = useModuleRelease();
  const [moduleContent, setModuleContent] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [moduleId]);

  useEffect(() => {
    // CRITICAL FIX: Wait for ModuleReleaseContext to load before checking access
    if (!releaseLoading) {
      fetchModuleData();
    }
  }, [moduleId, releaseLoading]);

  const fetchModuleData = async () => {
    try {
      setLoading(true);
      setError(null);
      setAccessDenied(false);
      
      // SIMPLIFIED: All modules are now accessible - focus on progress tracking only
      console.log(`🔓 UNLOCKED: All modules accessible - ${moduleId} granted access`);
      
      console.log(`✅ ACCESS GRANTED to module ${moduleId}`);
      
      // Fetch module content and quiz data in parallel
      const [moduleResponse, quizResponse] = await Promise.all([
        apiService.getModule(moduleId),
        apiService.getModuleQuiz(moduleId)
      ]);
      
      setModuleContent(moduleResponse.module);
      setQuizData(quizResponse.quiz);
      
      console.log(`📋 DEBUG: Module content loaded:`, moduleResponse.module?.title);
      console.log(`❓ DEBUG: Quiz data loaded:`, quizResponse.quiz ? 'YES' : 'NO');
      console.log(`🎯 DEBUG: showQuiz state:`, showQuiz);
      console.log(`✅ DEBUG: Should show quiz button:`, !showQuiz && quizResponse.quiz);
    } catch (err) {
      console.error('Error fetching module data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleReadStatus = async (sectionIndex) => {
    const isCurrentlyRead = isSectionRead(moduleId, sectionIndex);
    await markSectionAsRead(moduleId, sectionIndex, !isCurrentlyRead);
  };

  const handleMarkAsRead = (sectionId) => {
    console.log(`📖 PROGRESS: Marking section ${sectionId} as read for module ${moduleId}`);
    markSectionAsRead(moduleId, sectionId);
    console.log(`✅ PROGRESS: Section ${sectionId} marked as read`);
  };

  // Calculate overall progress including reading progress
  const getOverallProgress = () => {
    if (!moduleContent) return 0;
    
    const readingProgress = getModuleReadingProgress(moduleId);
    const readingPercentage = readingProgress.progressPercentage;
    const quizPercentage = quizCompleted ? 100 : (showQuiz ? 50 : 0);
    
    // Weight reading progress more heavily (70%) vs quiz (30%)
    return Math.round((readingPercentage * 0.7) + (quizPercentage * 0.3));
  };

  const handleDirectCompletion = async () => {
    console.log(`🚀 DIRECT COMPLETION: Starting direct completion for ${moduleId}`);
    
    try {
      // Call backend API directly
      const sessionId = localStorage.getItem('sessionId') || `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      console.log(`📡 DIRECT: Making API call to mark module completed...`);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/module-completion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: sessionId,
          moduleId: moduleId,
          quizCompleted: true,
          allSectionsRead: true
        })
      });
      
      if (response.ok) {
        console.log(`✅ DIRECT: Module completion API call successful!`);
        alert(`✅ Module ${moduleId} completed successfully! Module 2 should now be unlocked.`);
        
        // Redirect to landing page after short delay
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
        
      } else {
        const error = await response.text();
        console.error(`❌ DIRECT: API call failed:`, error);
        alert(`❌ Error completing module: ${error}`);
      }
      
    } catch (error) {
      console.error('❌ DIRECT: Direct completion failed:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleQuizComplete = async (score, quizResult) => {
    console.log(`🎯 MODULE PAGE: Quiz completed with score ${score}% for ${moduleId}`);
    setQuizCompleted(true);
    
    // Save quiz result to backend
    try {
      await apiService.saveQuizResult(quizResult);
      console.log(`✅ MODULE PAGE: Quiz result saved successfully`);
    } catch (err) {
      console.error('❌ MODULE PAGE: Error saving quiz result:', err);
    }
    
    // Auto-trigger direct completion if quiz passed
    if (score >= 60) {
      await handleDirectCompletion();
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <LoadingSpinner 
          size="large" 
          text={language === 'en' ? 'Loading module...' : 'Cargando módulo...'}
        />
      </div>
    );
  }

  if (accessDenied) {
    const moduleStatus = getModuleStatus(moduleId);
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center space-x-4 mb-8">
          <Link to="/">
            <Button variant="outline" size="sm" className="flex items-center space-x-2">
              <ArrowLeft className="w-4 h-4" />
              <span>{language === 'en' ? 'Back' : 'Volver'}</span>
            </Button>
          </Link>
        </div>
        
        <div className="text-center py-16">
          <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
            <Lock className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Module Locked' : 'Módulo Bloqueado'}
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            {moduleStatus === 'locked' 
              ? (language === 'en' 
                  ? 'This module will unlock after you complete the previous module.'
                  : 'Este módulo se desbloqueará después de que completes el módulo anterior.'
                )
              : (language === 'en'
                  ? 'This module is not yet available.'
                  : 'Este módulo aún no está disponible.'
                )
            }
          </p>
          <Link to="/">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
              {language === 'en' ? 'Return to Modules' : 'Volver a Módulos'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (error || !moduleContent) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center space-x-4 mb-8">
          <Link to="/">
            <Button variant="outline" size="sm" className="flex items-center space-x-2">
              <ArrowLeft className="w-4 h-4" />
              <span>{language === 'en' ? 'Back' : 'Volver'}</span>
            </Button>
          </Link>
        </div>
        <ErrorMessage 
          error={error || new Error(language === 'en' ? 'Module not found' : 'Módulo no encontrado')} 
          onRetry={fetchModuleData} 
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Navigation */}
      <div className="flex items-center space-x-4 mb-8">
        <Link to="/">
          <Button variant="outline" size="sm" className="flex items-center space-x-2">
            <ArrowLeft className="w-4 h-4" />
            <span>{language === 'en' ? 'Back' : 'Volver'}</span>
          </Button>
        </Link>
        <div className="flex-1">
          <Progress value={getOverallProgress()} className="h-2" />
        </div>
      </div>

      {/* Module Header */}
      <div className="text-center mb-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl flex items-center justify-center">
          <BookOpen className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
          {moduleContent.title[language]}
        </h1>
      </div>

      {!showQuiz ? (
        <>
          {/* Content Sections */}
          <div className="space-y-8 mb-12">
            {moduleContent.sections.map((section, index) => {
              const isRead = isSectionRead(moduleId, index);
              
              return (
                <Card key={index} className={`bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 ${isRead ? 'ring-2 ring-green-200' : ''}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl font-bold text-gray-800 flex items-center space-x-3">
                        <div className={`w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-md ${isRead ? 'from-green-500 to-green-600' : ''}`}>
                          {isRead ? <Check className="w-4 h-4" /> : (index + 1)}
                        </div>
                        <span>{section.title[language]}</span>
                      </CardTitle>
                      
                      <Button
                        onClick={() => handleToggleReadStatus(index)}
                        variant={isRead ? "default" : "outline"}
                        size="sm"
                        className={`flex items-center space-x-2 transition-all duration-200 ${
                          isRead 
                            ? 'bg-green-600 hover:bg-green-700 text-white' 
                            : 'border-gray-300 text-gray-600 hover:bg-green-50 hover:border-green-300'
                        }`}
                      >
                        {isRead ? (
                          <>
                            <BookOpenCheck className="w-4 h-4" />
                            <span>{language === 'en' ? 'Read' : 'Leído'}</span>
                          </>
                        ) : (
                          <>
                            <BookOpen className="w-4 h-4" />
                            <span>{language === 'en' ? 'Mark as Read' : 'Marcar como Leído'}</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      {section.content[language]}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Quiz CTA */}
          <div className="text-center bg-gradient-to-r from-green-100 to-blue-100 rounded-3xl p-8 mb-8">
            <Brain className="w-12 h-12 mx-auto mb-4 text-gray-700" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {language === 'en' ? 'Ready to Test Your Knowledge?' : '¿Listo para Evaluar tu Conocimiento?'}
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              {language === 'en'
                ? 'Take our interactive quiz to reinforce what you\'ve learned and get immediate feedback.'
                : 'Toma nuestro cuestionario interactivo para reforzar lo que has aprendido y obtener retroalimentación inmediata.'
              }
            </p>
            <Button 
              onClick={() => setShowQuiz(true)}
              size="lg"
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-3"
            >
              {language === 'en' ? 'Start Quiz' : 'Comenzar Cuestionario'}
            </Button>
          </div>
        </>
      ) : (
        <>
          <Quiz 
            quiz={quizData}
            moduleId={moduleId}
            onComplete={handleQuizComplete}
            onBack={() => setShowQuiz(false)}
          />
          
          {/* DIRECT COMPLETION SECTION - Always show */}
          <div className="mt-8 text-center bg-gradient-to-r from-blue-50 to-green-50 rounded-3xl p-8 border-2 border-blue-200">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {language === 'en' ? 'Complete This Module' : 'Completar Este Módulo'}
            </h2>
            <p className="text-gray-600 mb-6">
              {language === 'en' 
                ? 'Click here to mark this module as completed and unlock the next module.'
                : 'Haz clic aquí para marcar este módulo como completado y desbloquear el siguiente módulo.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleDirectCompletion}
                size="lg" 
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white min-w-[200px]"
              >
                {language === 'en' ? '✅ Complete Module' : '✅ Completar Módulo'}
              </Button>
              <Link to="/">
                <Button size="lg" variant="outline" className="min-w-[160px]">
                  {language === 'en' ? 'All Modules' : 'Todos los Módulos'}
                </Button>
              </Link>
            </div>
          </div>

          {/* Next Module Button - Show after quiz completion */}
          {quizCompleted && (
            <div className="mt-8 text-center bg-gradient-to-r from-green-100 to-blue-100 rounded-3xl p-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {language === 'en' ? 'Module Completed!' : '¡Módulo Completado!'}
              </h2>
              <p className="text-gray-600 mb-6">
                {language === 'en' 
                  ? 'Great job! You can now access the next module.'
                  : '¡Excelente trabajo! Ahora puedes acceder al siguiente módulo.'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button size="lg" variant="outline" className="min-w-[160px]">
                    {language === 'en' ? 'All Modules' : 'Todos los Módulos'}
                  </Button>
                </Link>
                <NextModuleButton currentModuleId={moduleId} />
              </div>
            </div>
          )}
        </>
      )}

      {/* Module Navigation */}
      {!showQuiz && (
        <div className="flex justify-between items-center pt-8 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            {language === 'en' ? 'Module Progress' : 'Progreso del Módulo'}
          </div>
          <Link to="/">
            <Button variant="outline">
              {language === 'en' ? 'All Modules' : 'Todos los Módulos'}
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default ModulePage;